import { useState } from 'react';

import {
  Button,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { ToolInvocation } from './ToolCallStatus';

interface ConversationStarterSuggestionCardProps {
  toolInvocation: ToolInvocation;
  onConfirm: (message: string) => void;
  onDismiss: (message: string) => void;
}

interface AvailablePreference {
  id: string;
  label: string;
}

interface StarterSuggestion {
  title: string;
  description: string;
  content: string;
  availablePreferences?: AvailablePreference[];
}

const NONE_VALUE = '__none__';

function parseSuggestion(
  toolInvocation: ToolInvocation,
): StarterSuggestion | null {
  const data =
    toolInvocation.state === 'output-available'
      ? toolInvocation.output
      : toolInvocation.input;

  if (!data || typeof data !== 'object') return null;

  const { title, description, content, availablePreferences } = data as Record<
    string,
    unknown
  >;
  if (
    typeof title !== 'string' ||
    typeof description !== 'string' ||
    typeof content !== 'string'
  ) {
    return null;
  }

  return {
    title,
    description,
    content,
    availablePreferences: Array.isArray(availablePreferences)
      ? (availablePreferences as AvailablePreference[])
      : undefined,
  };
}

export const ConversationStarterSuggestionCard = ({
  toolInvocation,
  onConfirm,
  onDismiss,
}: ConversationStarterSuggestionCardProps) => {
  const { t } = useTranslation();
  const [acted, setActed] = useState(false);
  const [selectedPreferenceId, setSelectedPreferenceId] = useState<
    string | undefined
  >(undefined);
  const isComplete = toolInvocation.state === 'output-available';

  if (!isComplete) {
    return (
      <Stack
        className="w-fit rounded-full border border-border-insights-subtle bg-bg-insights-subtle px-3 py-1.5"
        inline
        align="center"
        gap={2}
        fullWidth={false}
      >
        <Icon
          name="WandSparkles"
          className="animate-pulse text-text-insights"
        />
        <Text size="xs">{t('chat.starters.preparingSuggestion')}</Text>
      </Stack>
    );
  }

  const suggestion = parseSuggestion(toolInvocation);

  if (!suggestion) {
    return null;
  }

  return (
    <Stack
      gap={3}
      className="rounded-lg border border-border-primary bg-bg-secondary p-4"
    >
      <Stack gap={2} align="center" direction="horizontal">
        <Icon
          name="MessageSquarePlus"
          size="lg"
          className="text-text-tertiary"
        />
        <Text inline size="xs" weight="semibold" color="secondary">
          {t('chat.starters.suggestedTitle')}
        </Text>
      </Stack>

      <Stack
        gap={1}
        className="rounded-md border border-border-secondary bg-bg-primary p-3"
      >
        <Text size="xs" weight="semibold">
          {suggestion.title}
        </Text>
        <Text size="xs" color="secondary">
          {suggestion.description}
        </Text>
        <Text size="xs" color="tertiary" className="mt-1 italic">
          &ldquo;{suggestion.content}&rdquo;
        </Text>
      </Stack>

      {suggestion.availablePreferences &&
        suggestion.availablePreferences.length > 0 && (
          <Stack gap={1}>
            <Text size="xs" color="tertiary">
              {t('chat.starters.outputPreference')}
            </Text>
            <Select
              value={selectedPreferenceId ?? NONE_VALUE}
              onValueChange={(value) =>
                setSelectedPreferenceId(
                  value === NONE_VALUE ? undefined : value,
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('chat.starters.none')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  {t('chat.starters.none')}
                </SelectItem>
                {suggestion.availablePreferences.map((pref) => (
                  <SelectItem key={pref.id} value={pref.id}>
                    {pref.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>
        )}

      <Stack gap={2} direction="horizontal" justify="end">
        <Button
          variant="outline"
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            onDismiss(t('chat.starters.dismissMessage'));
          }}
        >
          {t('chat.starters.dismiss')}
        </Button>
        <Button
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            const pref = suggestion.availablePreferences?.find(
              (p) => p.id === selectedPreferenceId,
            );
            const msg = pref
              ? t('chat.starters.confirmWithPreferenceMessage', {
                  label: pref.label,
                })
              : t('chat.starters.confirmMessage');
            onConfirm(msg);
          }}
        >
          <Icon name="Plus" size="auto" />
          {t('chat.starters.save')}
        </Button>
      </Stack>
    </Stack>
  );
};
